// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import Button from "@turnerBlueprint/MobiusOverrides/Button/Button";
import GridContainer from "@turnerBlueprint/MobiusOverrides/GridContainer/GridContainer";
import { Divider } from "@turnerBlueprint/XDL/Utilities";
import React, { FC } from "react";
import { ResolveThunks, connect } from "react-redux";

const enum fields {
    showAsColumns = "showAsColumns",
    showCreditsSection = "showCreditsSection",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAsColumns]: boolean;
        [fields.showCreditsSection]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    return {
        allowGuestCheckout: allowGuestCheckout && (referredFromShipping || referredFromOnePageCheckout),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        returnUrl,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    WidgetProps &
    OwnProps &
    HasHistory;

export interface SignInPageLayoutStyles {
    container?: InjectableCss;
    signInSection?: InjectableCss;
    createAccountSection?: InjectableCss;
    applyForCreditSection?: InjectableCss;
    guestCheckoutSection?: InjectableCss;
}

export const signInPageLayoutStyles: SignInPageLayoutStyles = {};

const styles = signInPageLayoutStyles;

const SignInPageLayout: FC<Props> = ({
    id,
    history,
    allowGuestCheckout,
    currentUserIsGuest,
    returnUrl,
    signInAsGuest,
    fields,
}) => {
    const { showAsColumns, showCreditsSection } = fields;

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        if (currentUserIsGuest) {
            history.push(returnUrl);
        } else {
            signInAsGuest({ returnUrl });
        }
    };

    return (
        <StyledWrapper {...styles.container} {...{ showAsColumns }} data-test-selector="SignInPageLayout">
            <StyledWrapper {...styles.signInSection}>
                <XngZone contentId={id} zoneName="Content00" hint="SignInExistingAccount" />
            </StyledWrapper>
            <Divider
                margin={showAsColumns ? "0 30px" : "20px 0"}
                direction={showAsColumns ? "vertical" : "horizontal"}
            />
            <StyledWrapper {...styles.createAccountSection}>
                <XngZone contentId={id} zoneName="Content01" hint="CreateAccount" />
            </StyledWrapper>
            {!allowGuestCheckout && showCreditsSection && (
                <>
                    <Divider
                        margin={showAsColumns ? "0 30px" : "20px 0"}
                        direction={showAsColumns ? "vertical" : "horizontal"}
                        className="creditsDivider"
                    />
                    <StyledWrapper {...styles.applyForCreditSection}>
                        <XngZone contentId={id} zoneName="Content02" hint="ApplyCredit" />
                    </StyledWrapper>
                </>
            )}
            {allowGuestCheckout && (
                <>
                    <Divider
                        margin={showAsColumns ? "0 30px" : "20px 0"}
                        direction={showAsColumns ? "vertical" : "horizontal"}
                        className="creditsDivider"
                    />
                    <StyledWrapper {...styles.applyForCreditSection}>
                        <GridContainer gap={0}>
                            <GridItem width={12}>
                                <Typography variant="h3" as="h3" style={{ marginBottom: "12px" }}>
                                    {siteMessage("Custom_ContinueAsGuestTitle")}
                                </Typography>
                            </GridItem>
                            <GridItem width={12}>
                                <Typography size={14} lineHeight="22px" style={{ marginBottom: "20px" }}>
                                    {siteMessage("Custom_ContinueAsGuestText")}
                                </Typography>
                            </GridItem>
                            <GridItem width={12}>
                                <Button
                                    onClick={handleCheckoutAsGuest}
                                    data-test-selector="signInCheckoutAsGuest"
                                    aria-label="Checkout As Guest"
                                    variant="secondary"
                                >
                                    {translate("Continue As Guest")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInPageLayout)),
    definition: {
        group: "Layout" as any,
        displayName: "SignIn page layout",
        allowedContexts: ["SignInPage", "Header"],
        fieldDefinitions: [
            {
                name: fields.showAsColumns,
                displayName: "Show As Columns",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showCreditsSection,
                displayName: "Show Credits Section",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
